import { useTranslation } from 'react-i18next'
import { QualifiedProspectsFilters } from '../../types'
import { getQualifiedProspectsOverview } from '../../services'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Users, Route, AlertCircle, FileText, TrendingUp } from 'lucide-react'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    Legend,
} from 'recharts'
import { SCHEMES } from '@/components/EchartCustomized'

interface OverviewProps {
    filters: QualifiedProspectsFilters
}

const JOURNEY_COLORS: Record<string, string> = {
    searching: SCHEMES.info[0],
    contact: SCHEMES.brand[5],
    presentation: SCHEMES.warning[0],
    poc: SCHEMES.success[2],
    deal: SCHEMES.brand[6],
    contract: SCHEMES.success[0],
    'no-journey': SCHEMES.mono.gray[2],
}

const Overview: React.FC<OverviewProps> = ({ filters }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'qualified-prospects-overview',
                filters.from_date,
                filters.to_date,
            ],
            queryFn: () => getQualifiedProspectsOverview(filters),
            enabled: !!filters.from_date && !!filters.to_date,
        },
        {
            loadingComponent: (
                <div className="flex justify-center items-center h-[400px]">
                    <Loading.CircularProgress width={32} height={32} />
                </div>
            ),
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!data) {
        return (
            <div className="flex justify-center items-center h-[300px] text-gray-500">
                {t('no_data_available')}
            </div>
        )
    }

    const { stats, journey_distribution, status_distribution } = data

    // KPI Cards data
    const kpiCards = [
        {
            label: t('qualified_prospects.total_prospects'),
            value: stats.total_prospects,
            icon: Users,
            color: 'bg-blue-50 text-blue-600',
            iconBg: 'bg-blue-100',
        },
        {
            label: t('qualified_prospects.with_journey'),
            value: stats.prospects_with_journey,
            icon: Route,
            color: 'bg-green-50 text-green-600',
            iconBg: 'bg-green-100',
        },
        {
            label: t('qualified_prospects.without_journey'),
            value: stats.prospects_without_journey,
            icon: AlertCircle,
            color: 'bg-amber-50 text-amber-600',
            iconBg: 'bg-amber-100',
        },
        {
            label: t('qualified_prospects.with_recent_notes'),
            value: stats.prospects_with_recent_notes,
            icon: FileText,
            color: 'bg-purple-50 text-purple-600',
            iconBg: 'bg-purple-100',
        },
        {
            label: t('qualified_prospects.inactive'),
            value: stats.prospects_inactive,
            icon: TrendingUp,
            color: 'bg-red-50 text-red-600',
            iconBg: 'bg-red-100',
        },
    ]

    // Prepare journey distribution data for funnel chart
    const funnelData = journey_distribution.map((item) => ({
        name: t(`qualified_prospects.journey_steps.${item.journey_step}`),
        value: item.customer_count,
        percentage: item.percentage,
        fill: JOURNEY_COLORS[item.journey_step] || SCHEMES.mono.gray[2],
    }))

    // Prepare pie chart data for status distribution
    const statusData = status_distribution.reduce(
        (acc, item) => {
            const existing = acc.find((a) => a.name === item.step_status)
            if (existing) {
                existing.value += item.customer_count
            } else {
                acc.push({
                    name: item.step_status,
                    value: item.customer_count,
                })
            }
            return acc
        },
        [] as { name: string; value: number }[]
    )

    const STATUS_COLORS = [
        SCHEMES.success[0],
        SCHEMES.warning[0],
        SCHEMES.info[0],
        SCHEMES.error[0],
        SCHEMES.mono.gray[2],
        SCHEMES.brand[5],
    ]

    return (
        <div className="space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {kpiCards.map((card) => (
                    <div
                        key={card.label}
                        className={`${card.color} rounded-lg p-4 border border-gray-100 shadow-sm`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`${card.iconBg} p-2 rounded-lg`}>
                                <card.icon size={20} />
                            </div>
                            <div>
                                <Typogaphy className="text-2xl font-bold">
                                    {card.value.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-600">
                                    {card.label}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Journey Distribution Funnel */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                        {t('qualified_prospects.journey_distribution')}
                    </Typogaphy>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={funnelData}
                            layout="vertical"
                            margin={{ left: 20, right: 40 }}
                        >
                            <XAxis
                                type="number"
                                stroke="#9ca3af"
                                fontSize={11}
                            />
                            <YAxis
                                dataKey="name"
                                type="category"
                                stroke="#9ca3af"
                                fontSize={11}
                                width={100}
                                tickLine={false}
                            />
                            <Tooltip
                                formatter={(value: number) => [
                                    value.toLocaleString(),
                                    t('customers'),
                                ]}
                                labelFormatter={(label: string) => {
                                    const item = funnelData.find(
                                        (d) => d.name === label
                                    )
                                    return item
                                        ? `${label} (${item.percentage.toFixed(1)}%)`
                                        : label
                                }}
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                }}
                            />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {funnelData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.fill}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Status Distribution Pie */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                        {t('qualified_prospects.status_distribution')}
                    </Typogaphy>
                    {statusData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={(props: any) =>
                                        `${t(`qualified_prospects.statuses.${props.name}`)} (${(props.percent * 100).toFixed(0)}%)`
                                    }
                                    labelLine={false}
                                >
                                    {statusData.map((_, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={
                                                STATUS_COLORS[
                                                    index % STATUS_COLORS.length
                                                ]
                                            }
                                        />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value: number) => [
                                        value.toLocaleString(),
                                        t('customers'),
                                    ]}
                                />
                                <Legend
                                    formatter={(value) =>
                                        t(
                                            `qualified_prospects.statuses.${value}`
                                        )
                                    }
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex justify-center items-center h-[300px] text-gray-500">
                            {t('no_data_available')}
                        </div>
                    )}
                </div>
            </div>

            {/* Status Detail Table */}
            {status_distribution.length > 0 && (
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                        {t('qualified_prospects.status_detail')}
                    </Typogaphy>
                    <div className="overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('qualified_prospects.journey_step')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('qualified_prospects.status')}
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        {t(
                                            'qualified_prospects.customer_count'
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {status_distribution.map((item, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                                style={{
                                                    backgroundColor:
                                                        JOURNEY_COLORS[
                                                            item.journey_step
                                                        ] + '20',
                                                    color: JOURNEY_COLORS[
                                                        item.journey_step
                                                    ],
                                                }}
                                            >
                                                {t(
                                                    `qualified_prospects.journey_steps.${item.journey_step}`
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                            {t(
                                                `qualified_prospects.statuses.${item.step_status}`
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                            {item.customer_count.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Overview
